<template>
  <b-modal
    v-model="localValue"
    :title="props.modalTitle"
    id="simplified-tree-list-modal"
    body-class="simplified-tree-list-body"
    header-close-content=""
    @ok="confirmSelection"
    @cancel="emits('cancel')"
    @close="emits('close')"
    @hide="hideHandler"
    @show="onShow">
    <div class="bread-crumbs" v-if="!props.noBreadCrumbs">
      <PathPresenter reducedVisual v-model="pathPresenterData" @dots-clicked="switchBranchUp" dots-link-style data-cy="multi-level-modal-path-presenter" />
    </div>

    <CommonFilter
      no-max-width
      prepend-icon="Filter"
      :placeholder="$t('Filter')"
      v-model="vm.filter"
      data-cy="multi-level-modal-common-filter"
      trigger-by-time />

    <div class="list-container" ref="listContainer" data-cy="multi-level-modal-container">
      <UseVirtualList ref="virtualList" :list="combinedList" height="calc(80vh - 150px)" :options="{ itemHeight: 30 }">
        <template #default="{ data }">
          <div v-if="data.type == 'all'" class="entry" :class="{ active: vm.selection?.id == data.id }" @click="selectEntry(data)">
            <div class="entry-icon" v-if="data.icon">
              <Icon :icon="data.icon" />
            </div>
            <div class="entry-text">
              <span>{{ data.name }}</span>
            </div>
          </div>
          <div
            v-if="data.type == 'branch'"
            @dblclick="switchBranchDown(data.id)"
            class="entry branch"
            :class="{ active: vm.selection?.id == data.id }"
            @click="selectEntry(data)">
            <div class="entry-icon" v-if="data.icon">
              <Icon :icon="data.icon" />
            </div>
            <div class="entry-text">
              <span>{{ data.name }}</span>
            </div>
            <div class="entry-action" v-if="data.type == 'branch' && !data.hasNoChildren">
              <b-btn class="btn-icon mono" @click="switchBranchDown(data.id)">
                <Icon icon="ChevronRight" />
              </b-btn>
            </div>
          </div>
          <hr class="divider" v-if="data.type == 'divider'" />
          <div v-if="data.type == 'leaf'" class="entry leaf" :class="{ active: vm.selection?.id == data.id }" @click="selectEntry(data)">
            <div class="entry-icon" v-if="data.icon">
              <Icon :icon="data.icon" />
            </div>
            <div class="entry-text">
              <span>{{ data.name }}</span>
            </div>
          </div>
        </template>
      </UseVirtualList>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn
        @click="ok"
        data-cy="multi-level-modal-select-btn"
        variant="primary"
        :disabled="!vm.selection || !_.includes([...(withAll || rootSelection ? ['all'] : []), ...props.selectableType], vm.selection.type)">
        {{ $t("select") }}
      </b-btn>
      <b-btn @click="cancel" data-cy="multi-level-modal-close-btn">{{ $t("close") }}</b-btn>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { AttributeDataType } from "@medapro-types/common/common-enum";
import { Branch, Leaf, MultiLevelNavigationConfirmModel, MultiLevelNavigationSelectionModel } from "@medapro-types/common/components/MultiLevelNavigation";
import { PathPresentedDataModel } from "@medapro-types/common/components/PathPresenter";
import { UseVirtualList } from "@vueuse/components";
import { UseVirtualListReturn } from "@vueuse/core";
import _ from "lodash";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import CommonFilter from "../CommonFilter.vue";
import Icon from "../Icon.vue";
import PathPresenter from "../PathPresenter.vue";

//#region General
const { t: $t } = useI18n();

const emits = defineEmits(["input", "confirm", "cancel", "close", "hide"]);
const props = defineProps<{
  value: boolean;
  modalTitle: string;
  fetchBranches: (branchId: string | null | undefined) => Promise<Branch[]>;
  fetchLeaves: (branchId: string | null | undefined) => Promise<Leaf[]>;
  selectableType: ("leaf" | "branch")[];
  noBreadCrumbs?: boolean;
  withAll?: boolean;
  selectedEntryId: string | null | undefined;
  selectedPath?: Branch[] | null | undefined;
  basePathTitle?: string;
  rootSelection?: MultiLevelNavigationSelectionModel | null;
}>();
//#endregion

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});
//#endregion

//#region Refs
const listContainer = ref<HTMLDivElement | null>(null);
const virtualList = ref<UseVirtualListReturn<unknown[]> | null>(null);
//#endregion

//#region Viewmodel
const allEntry: MultiLevelNavigationSelectionModel = {
  id: null,
  name: `<${$t("all")}>`,
  type: "all",
  dataType: AttributeDataType.AttributeDataTypeAlphanumerical
};
const defaultSelection = () => (props.withAll ? allEntry : (props.rootSelection ?? null));
type ViewModel = {
  filter: string;
  selection: MultiLevelNavigationSelectionModel | null;
  branches: Branch[];
  leaves: Leaf[];
  branchPath: Branch[];
  currentBranch: Branch | null;
};
const vm = reactive<ViewModel>({
  filter: "",
  selection: defaultSelection(),
  branches: [],
  leaves: [],
  branchPath: [],
  currentBranch: null
});
//#endregion

//#region Branches
const switchBranchRoot = async () => {
  vm.currentBranch = null;
  await switchBranch();
  vm.branchPath = [];
};

const switchBranchUp = async () => {
  const lastEntry = _.last(vm.branchPath);
  if (lastEntry) {
    vm.currentBranch = <Branch | null>(vm.branchPath.pop() ?? null);
    if (lastEntry.parentId) await switchBranch(lastEntry.parentId);
    else await switchBranch(_.last(vm.branchPath)?.id);
  }
};

const switchBranchDown = async (branchId: string) => {
  const _branch = vm.branches.find((x) => x.id == branchId);
  if (_branch) {
    const doSwitch = await switchBranch(branchId);
    if (doSwitch) {
      vm.currentBranch = _branch;
      vm.branchPath.push(_branch);
    }
  }
};

const switchBranch = async (branchId?: string | null | undefined) => {
  const fetchedBranches = await props.fetchBranches(branchId);
  const fetchedLeaves = await props.fetchLeaves(branchId);

  const doSwitch = !!fetchedBranches.length || !!fetchedLeaves.length;
  if (doSwitch) {
    vm.branches = fetchedBranches;
    vm.leaves = fetchedLeaves;
    vm.selection = defaultSelection();
    virtualList.value?.scrollTo(0);
  } else {
    const foundBranch = vm.branches.find((x) => x.id == branchId);
    if (foundBranch) foundBranch.hasNoChildren = true;
  }

  return doSwitch;
};

const pathPresenterData = computed<PathPresentedDataModel[]>(() => {
  const _home: PathPresentedDataModel = {
    id: "\\",
    name: props.basePathTitle ?? "",
    icon: "Home",
    click: switchBranchRoot
  };

  if (vm.branchPath && vm.branchPath.length && vm.branchPath.length > 0) _home.name = "";

  return [_home, ...vm.branchPath.map((x) => ({ id: x.id, name: x.name ?? "" }))];
});

//#endregion

//#region Selection

const selectEntry = (data: MultiLevelNavigationSelectionModel) => {
  vm.selection = data;
};

const restoreSelection = () => {
  if (props.selectedEntryId) {
    const foundIndex = combinedList.value.findIndex((x) => x.id == props.selectedEntryId);
    if (foundIndex != -1) {
      vm.selection = combinedList.value[foundIndex];
      virtualList.value?.scrollTo(foundIndex);
    }
  }
};

const hideHandler = () => {
  resetData();
  emits("hide");
};

const resetData = () => {
  Object.assign(vm, {
    filter: "",
    selection: null,
    branches: [],
    leaves: [],
    branchPath: [],
    currentBranch: null
  });
};

const confirmSelection = () => {
  const result: MultiLevelNavigationConfirmModel = { paths: vm.branchPath, selection: vm.selection };
  emits("confirm", result);
};
//#endregion

//#region Filter
const combinedList = computed<MultiLevelNavigationSelectionModel[]>(() => {
  let result = _.chain<MultiLevelNavigationSelectionModel>([]);
  if (props.withAll && _.isEmpty(vm.branchPath)) {
    result = result.concat([allEntry, { id: "-1", name: "divider", type: "divider" }]);
  }
  if (props.rootSelection) {
    result = result.concat([props.rootSelection, { id: "-1", name: "divider", type: "divider" }]);
  }
  const _branches: MultiLevelNavigationSelectionModel[] = vm.branches.map((x) => {
    return { id: x.id, name: x.name ?? "", type: "branch", hasNoChildren: x.hasNoChildren, icon: x.icon };
  });

  const _leaves: MultiLevelNavigationSelectionModel[] = vm.leaves.map((x) => {
    return { id: x.id, name: x.name, type: "leaf", dataType: x.dataType, icon: x.icon };
  });

  result = result.concat(_branches);
  if (_branches.length && _leaves.length) result = result.concat([{ id: "-1", name: "divider", type: "divider" }]);
  result = result.concat(_leaves);

  if (vm.filter?.length) result = result.filter((x) => !!x?.name && x.name.toLowerCase().includes(vm.filter.toLowerCase()));

  return result.value();
});

//#endregion

async function onShow() {
  let id: string | undefined;
  if (props.selectedPath && !_.isEmpty(props.selectedPath)) {
    const branches = _.cloneDeep(props.selectedPath.filter((x) => x.id !== ""));
    const foundBranch = _.maxBy(branches, (x) => x.depth);
    if (foundBranch) {
      vm.branchPath = branches;
      id = foundBranch.id;
    }
  }
  await switchBranch(id);

  restoreSelection();
}
</script>
